import React, { useState, useEffect } from 'react';
import { FaUserShield, FaUsers } from 'react-icons/fa';
import { toast } from 'react-toastify';
import UserGroupService, { User, Group } from '../../../services/permissionService';
import { formatUserDisplayName } from '../../../utils/permissionUtils';
import { personalakteStyles as ps, colors } from '../../employees/styles/personalakteStyles';

const getGroupBadgeColor = (groupName: string): { bg: string; text: string } => {
  const name = groupName.toLowerCase();
  if (name.includes('admin')) return { bg: colors.dangerLight, text: '#991b1b' };
  if (name.includes('manager')) return { bg: colors.infoLight, text: '#1e40af' };
  if (name.includes('employee') || name.includes('mitarbeiter')) return { bg: colors.successLight, text: '#065f46' };
  return { bg: colors.bgMuted, text: colors.textBody };
};

export default function PermissionsTab(): React.JSX.Element {
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [fetchedUsers, fetchedGroups] = await Promise.all([
        UserGroupService.fetchAllUsers(),
        UserGroupService.fetchAllGroups(),
      ]);
      const usersWithGroups = fetchedUsers.filter((u) => u.groups.length > 0);
      setUsers(usersWithGroups);
      setGroups(fetchedGroups);
    } catch (err) {
      console.error('Failed to load permissions data:', err);
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted }}>Loading...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: colors.navy }}>
            User permissions ({users.length})
          </h3>
          <p style={{ margin: '4px 0 0 0', fontSize: 12, color: colors.textMuted }}>
            Manage user roles and access permissions
          </p>
        </div>
      </div>

      {/* Groups Summary */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {groups.map((group) => {
          const badgeColors = getGroupBadgeColor(group.name);
          const memberCount = users.filter((u) => u.groups.some((g) => g.id === group.id)).length;
          return (
            <div key={group.id} style={{ ...ps.cardCompact, padding: '14px 20px', minWidth: 130 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <FaUsers size={13} color={badgeColors.text} />
                <span style={{ fontWeight: 600, fontSize: 12, color: badgeColors.text }}>{group.name}</span>
              </div>
              <div style={{ ...ps.kpiValue, fontSize: 22 }}>{memberCount}</div>
              <div style={{ fontSize: 11, color: colors.textLight }}>
                {memberCount === 1 ? 'member' : 'members'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Users Table */}
      {users.length === 0 ? (
        <div style={{ ...ps.innerContainer, padding: 60, textAlign: 'center' }}>
          <FaUserShield size={28} style={{ marginBottom: 12, color: colors.textLight }} />
          <p style={{ margin: 0, fontSize: 15, color: colors.textMuted }}>No users with permissions</p>
          <p style={{ margin: '8px 0 0 0', fontSize: 13, color: colors.textLight }}>
            Assign roles to users in the employee settings.
          </p>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>User</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Roles</th>
              <th style={thStyle}>Work area access</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="hover-list-item" style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                <td style={tdStyle}>
                  <span style={{ fontWeight: 600, color: colors.navy }}>{formatUserDisplayName(user)}</span>
                </td>
                <td style={tdStyle}>{user.email || '—'}</td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {user.groups.map((group) => {
                      const c = getGroupBadgeColor(group.name);
                      return (
                        <span key={group.id} style={ps.badge(c.bg, c.text)}>{group.name}</span>
                      );
                    })}
                  </div>
                </td>
                <td style={tdStyle}>
                  {user.has_unrestricted_work_area_access ? (
                    <span style={ps.badge(colors.successLight, '#065f46')}>All areas</span>
                  ) : user.work_area_restrictions && user.work_area_restrictions.length > 0 ? (
                    <span style={{ fontSize: 12, color: colors.textMuted }}>
                      {user.work_area_restrictions.length} area{user.work_area_restrictions.length !== 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, color: colors.textLight }}>None</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Info Box */}
      <div
        style={{
          marginTop: 20,
          padding: 14,
          background: colors.infoLight,
          borderRadius: 10,
          border: `1px solid ${colors.info}20`,
        }}
      >
        <p style={{ margin: 0, fontSize: 12, color: '#0369a1' }}>
          <strong>Tip:</strong> To change user permissions, go to the employee's detail page and
          edit their role settings there. Admins can be managed via the admin overview page.
        </p>
      </div>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: colors.textLight,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  borderBottom: `1px solid ${colors.border}`,
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: 13,
  color: colors.textBody,
};
